// formio-interceptor.service.ts
import { Injectable } from '@angular/core';
import { Formio } from '@formio/angular';
import { environment } from '../../../environments/environment';
import { TabSdk } from '@techextensor/tab-sdk';
import { StorageConstants } from '@techextensor/tab-core-utility';
import { Constants, TabCoreScreens } from '../const/constants';

@Injectable({
    providedIn: 'root'
})
export class FormioInterceptorService {
    private originalFetch: typeof Formio.fetch;

    constructor() { }

    /**
     * Intercepts the Formio fetch method to add an environment header
     */
    intercept() {
        // Patch the Formio fetch method
        this.patchFormioFetch();

        // Check if the Formio Components object exists
        if ((window as any)?.Formio?.Components) {
            this.overrideFormioButton();
            this.overrideFormioPassword();
            this.overrideFormioHtml();
            this.overrideFormioSelect();
            this.overrideFormioFile();
        }
    }

    /**
     * Patch the Formio fetch method to add an environment header
     */
    private patchFormioFetch() {
        // Store original fetch
        this.originalFetch = Formio.fetch;

        /**
         * Override Formio's fetch method to add an environment header
         * @param url URL to fetch
         * @param options Fetch options
         * @returns Promise that resolves to the response
         */
        Formio.fetch = async (url: string, options: any = {}) => {
            // Add headers if they don't exist
            if (!options.headers) {
                options.headers = {};
            }

            const skipInterceptor = options.headers['x-skip-interceptor'];
            if (!skipInterceptor) {
                if (TabSdk.store.token) {
                    options.headers[StorageConstants.Authorization] = TabSdk.store.token;

                } else if (TabSdk.store.staticToken && !TabSdk.store.token) {
                    options.headers[StorageConstants.Authorization] = TabSdk.store.staticToken;

                }
                options.headers[StorageConstants.applicationCode] = TabSdk.store.appName ?? environment.commonConfig.applicationCode;
                options.headers[StorageConstants.TenantId] = TabSdk.store.tenantId ?? environment.commonConfig.tenantId;
                options.headers[StorageConstants.AppId] = TabSdk.store.appId ?? environment.commonConfig.applicationId;
                options.headers[StorageConstants.Environment] = TabSdk.store.environmentId ?? environment.id;

            } else {
                // Remove the custom header marker
                delete options.headers['x-skip-interceptor'];
            }


            // Call original fetch with modified options
            return this.originalFetch(url, options);
        };
    }

    /**
     * Override the Formio Button component to remove the message container
     * on attach and only for submit buttons
     */
    private overrideFormioButton() {
        // Get the original Button component
        const OriginalButton: any = (window as any)?.Formio.Components.components.button;

        /**
         * Extend it with custom functionality
         */
        class TabCustomButton extends OriginalButton {
            // Override attach to modify behavior
            attach(element: any) {
                // Call the original attach method to set up refs
                const result = super.attach(element);

                // Only modify submit button behavior
                if (this['component']?.action === 'submit') {
                    // Remove the message container if it exists
                    if (this['refs']?.buttonMessageContainer) {
                        this['refs'].buttonMessageContainer.remove();
                    }

                    // Add immediate click handling for submit buttons
                    this.addClickHandler();
                    this.addSubmitEventListeners();
                }

                // Handle custom buttons with spin class
                if (this['component']?.action === 'custom' && this['component']?.customClass?.includes('spin-button')) {
                    this.addClickHandler();
                }

                return result;
            }

            /**
             * Add click handler for submit buttons
             */
            addClickHandler() {
                const button = this['refs']?.button;
                if (!button) return;

                button.addEventListener('click', (event: Event) => {
                    // If already processing, prevent the click
                    if (button.getAttribute('data-submitting') === 'true') {
                        event?.preventDefault();
                        event?.stopPropagation();
                        event?.stopImmediatePropagation();
                        return false;
                    }

                    // Set submitting state immediately on click
                    button?.setAttribute('data-submitting', 'true');

                    return true;
                }, true); // Use capture phase to execute before other handlers
            }

            /**
             * Adds event listeners for Formio form submission to the button.
             * If form submission fails, the button's data-submitting attribute is removed.
             */
            addSubmitEventListeners() {
                const button = this['refs']?.button;
                if (!button) return;

                // Listen for the form submission event
                this['on']('submitError', () => {
                    button?.removeAttribute('data-submitting');
                }, true);

                this['on']('submitDone', () => {
                    button?.removeAttribute('data-submitting');
                }, true);
            }
        }

        // Replace the original Button component
        (window as any).Formio.Components.components.button = TabCustomButton;
    }

    /**
     * Overrides the Formio Password component to add a toggle visibility feature.
     */
    private overrideFormioPassword() {
        // Get the original Password component
        const OriginalPassword: any = (window as any).Formio.Components.components.password;

        /**
         * Extend the original Password component with custom functionality
         */
        class TabCustomPassword extends OriginalPassword {
            /**
             * Override the attach method to add a toggle visibility icon.
             * @param element - The DOM element to attach the component to.
             */
            attach(element: any) {
                // Call the original attach method
                const result = super.attach(element);

                // Access the input element after DOM is rendered
                const input = this['refs']?.input?.[0];
                if (input) {
                    // Adjust padding for the toggle icon
                    input.style.paddingRight = '2.5rem';

                    // Create the toggle visibility icon
                    const toggleIcon = document.createElement('i');
                    toggleIcon.classList.add('e-icons', 'e-eye');
                    toggleIcon.style.position = 'absolute';
                    toggleIcon.style.right = '10px';
                    toggleIcon.style.top = '50%';
                    toggleIcon.style.transform = 'translateY(-50%)';
                    toggleIcon.style.cursor = 'pointer';
                    toggleIcon.style.zIndex = '10';

                    // Ensure the parent element is positioned relatively
                    const wrapper = input.parentElement;
                    if (wrapper) {
                        wrapper.style.position = 'relative';
                        wrapper.appendChild(toggleIcon);

                        // Add click event to toggle password visibility
                        toggleIcon.addEventListener('click', () => {
                            const isHidden = input.type === 'password';
                            input.type = isHidden ? 'text' : 'password';
                            toggleIcon.classList.toggle('e-eye');
                            toggleIcon.classList.toggle('e-eye-slash');
                        });
                    }
                }

                return result;
            }
        }

        // Replace the original Password component with the extended version
        (window as any).Formio.Components.components.password = TabCustomPassword;
    }

    private overrideFormioHtml() {
        // Get the original HTML component
        const OriginalHtml: any = (window as any)?.Formio.Components.components.htmlelement;

        class TabCustomHtml extends OriginalHtml {
            constructor(component: any, options: any, data: any) {
                // Modify the component schema before calling super
                if (component.tag === 'p' || !component.tag) {
                    component.tag = 'div';
                }

                super(component, options, data);
            }
        }

        // Replace the original HTML component
        (window as any).Formio.Components.components.htmlelement = TabCustomHtml;
    }

    /**
     * Override the Formio select component to add additional functionality.
     * Currently, this override adds a feature to dynamically hide the placeholder
     * based on whether values exist in the select component.
     */
    private overrideFormioSelect() {
        const OriginalSelect = (window as any).Formio.Components.components.select;

        class CustomSelect extends OriginalSelect {
            /**
             * Attach the select component to the DOM.
             * @param element The element to attach the component to.
             * @returns The result of the original attach method.
             */
            attach(element: any) {
                const result = super.attach(element);
                if (this['component']?.multiple) {
                    // Initial hide placeholder if values exist
                    this.togglePlaceholder(this['dataValue']);

                    // Listen for changes to update placeholder visibility dynamically
                    this['on']('change', () => {
                        this.togglePlaceholder(this['dataValue']);
                    });
                }

                return result;
            }

            /**
             * Toggle the placeholder visibility based on the value.
             * If the value is not empty, hide the placeholder.
             * Otherwise, display the placeholder.
             * @param value The value to check.
             */
            togglePlaceholder(value: any) {
                const input = this['focusableElement'];
                if (!input) return;

                // Hide placeholder if value is not empty
                if (value && (Array.isArray(value) ? value.filter((item: any) => item?.length)?.length : true)) {
                    input.placeholder = '';
                } else {
                    input.placeholder = this['component'].placeholder || '';
                }
            }
        }

        (window as any).Formio.Components.components.select = CustomSelect;
    }

    /**
     * Override the Formio File component to add headers for all operations
     */
    private overrideFormioFile() {
        const OriginalFile: any = (window as any)?.Formio.Components.components.file;

        class TabCustomFile extends OriginalFile {
            private blobUrlCache: Map<string, Promise<string>> = new Map();
            private mutationObserver: MutationObserver | null = null;

            // ==================== UTILITY METHODS ====================

            /**
             * Get dynamic authentication headers for API requests
             */
            private getAuthHeaders(): Record<string, string> {
                const headers: any = {};

                if (TabSdk.store.token) {
                    headers[StorageConstants.Authorization] = TabSdk.store.token;
                } else if (TabSdk.store.staticToken) {
                    headers[StorageConstants.Authorization] = TabSdk.store.staticToken;
                }

                headers[StorageConstants.applicationCode] = TabSdk.store.appName ?? environment.commonConfig.applicationCode;
                headers[StorageConstants.TenantId] = TabSdk.store.tenantId ?? environment.commonConfig.tenantId;
                headers[StorageConstants.AppId] = TabSdk.store.appId ?? environment.commonConfig.applicationId;
                headers[StorageConstants.Environment] = TabSdk.store.environmentId ?? environment.id;

                if (this['component']?.screen) {
                    headers[StorageConstants.Screen] = this['component'].screen;
                }

                return headers;
            }

            /**
             * Check if a file is an image based on type or extension
             */
            private isImageFile(file: any): boolean {
                if (!file) return false;
                
                const fileName = file.originalName || file.name || file.DocumentName || 
                            file.MediaId?.Title || file.URL || file.url || '';
                
                // Check extension first (handles incorrect MIME types)
                if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName)) {
                    return true;
                }
                
                // Fallback to type property
                const type = file.type || '';
                return type.includes('image/');
            }

            /**
             * Build GetImage URL from file path
             */
            private buildGetImageUrl(filePath: string): string {
                return `${environment.apiUrl}${Constants.FILEMANAGER_URL}${Constants.GETIMAGE}?path=${encodeURIComponent(filePath)}`;
            }

            /**
             * Get MIME type from filename
             */
            private getFileType(fileName: string): string {
                if (!fileName) return 'application/octet-stream';
                
                const ext = fileName.split('.').pop()?.toLowerCase();
                const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
                
                if (imageExts.includes(ext || '')) {
                    return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
                }
                
                if (ext === 'pdf') return 'application/pdf';
                
                return 'application/octet-stream';
            }

            /**
             * Extract file path from various possible object structures
             */
            private extractFilePath(file: any): string {
                return file.mediaUrl ||      // NEW: Check mediaUrl first (FTP path from upload response)
                    file.URL || 
                    file.url || 
                    file.MediaId?.URL || 
                    file.data?.MediaId?.URL || 
                    file.data?.path || 
                    file.data?.Path || 
                    file.path || '';
            }

            /**
             * Fetch file with authentication using GetImage endpoint with caching
             */
            private fetchFileWithCache(file: any): Promise<string> {
                const filePath = this.extractFilePath(file);
                
                if (!filePath) {
                    console.error('No file path found in file object:', file);
                    return Promise.resolve('');
                }

                // Return cached promise if available
                if (this.blobUrlCache.has(filePath)) {
                    return this.blobUrlCache.get(filePath)!;
                }

                const fetchPromise = fetch(this.buildGetImageUrl(filePath), {
                    method: 'GET',
                    headers: this.getAuthHeaders()
                })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch file: ${response.statusText}`);
                    }
                    return response.blob();
                })
                .then(blob => URL.createObjectURL(blob))
                .catch(error => {
                    console.error('Error fetching file for display:', error);
                    this.blobUrlCache.delete(filePath);
                    return '';
                });

                this.blobUrlCache.set(filePath, fetchPromise);
                return fetchPromise;
            }

            /**
             * Find matching file from dataValue by URL or path
             */
            private findFileByUrl(url: string): any {
                const files = Array.isArray(this['dataValue']) ? this['dataValue'] : [this['dataValue']];
                return files.find((f: any) => {
                    if (!f) return false;
                    return (f.url && url.includes(f.url)) || 
                        (f.URL && url.includes(f.URL)) ||
                        (f.mediaUrl && url.includes(f.mediaUrl)) ||  // NEW: Check mediaUrl
                        (f.MediaId?.URL && url.includes(f.MediaId.URL)) ||
                        (f.data?.MediaId?.URL && url.includes(f.data.MediaId.URL)) ||
                        (f.data?.path && url.includes(f.data.path));
                });
            }

            /**
             * Get all files from dataValue as array
             */
            private getAllFiles(): any[] {
                return Array.isArray(this['dataValue']) ? this['dataValue'] : 
                    (this['dataValue'] ? [this['dataValue']] : []);
            }

            // ==================== WEBCAM OVERRIDE ====================

            get useWebcam(): boolean {
                return !!(this['component'].webcam && this['component'].capture !== false);
            }

            get hasWebcam(): boolean {
                return this.useWebcam;
            }

            // ==================== RENDERING OVERRIDES ====================

            /**
             * Override render to enable webcam button while preserving image display setting
             */
            render(...args: any[]): any {
                const originalImage = this['component'].image;
                
                // Temporarily enable image for webcam button rendering
                if (this['component'].webcam && !originalImage) {
                    this['component'].image = true;
                }
                
                const result = super.render(...args);
                
                // Restore original setting
                this['component'].image = originalImage;
                
                return result;
            }

            /**
             * Override file rendering to display as link when image is disabled
             */
            renderFile(file: any): string {
                if (!this['component'].image) {
                    const fileName = file.originalName || file.name || 'Download';
                    return `<a href="${file.url}" ref="fileLink" target="_blank">${fileName}</a>`;
                }
                
                return super.renderFile ? super.renderFile(file) : '';
            }

            // ==================== FILE OPERATIONS ====================

            /**
             * Override the main upload method to inject auth headers
             */
            upload(files: any) {
                if (this['component'].storage === 'url') {
                    if (!this['component'].options) {
                        this['component'].options = {};
                    }
                    if (!this['component'].options.headers) {
                        this['component'].options.headers = {};
                    }
                    
                    Object.assign(this['component'].options.headers, this.getAuthHeaders());
                }

                return super.upload(files);
            }

            /**
             * Override uploadFile to handle different file formats and inject auth headers
             */
            uploadFile(file: any, fileName: any): Promise<any> {
                if (this['component'].storage !== 'url') {
                    return super.uploadFile ? super.uploadFile(file, fileName) : Promise.reject('Upload not supported');
                }

                const url = this['component'].url;
                const authHeaders = this.getAuthHeaders();
                
                return new Promise((resolve, reject) => {
                    let fileToUpload = file;
                    
                    // Convert non-Blob/File to Blob
                    if (!(file instanceof Blob) && !(file instanceof File)) {
                        if (typeof file === 'string') {
                            try {
                                // Handle data URL
                                if (file.startsWith('data:')) {
                                    const arr = file.split(',');
                                    const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
                                    const bstr = atob(arr[1]);
                                    const u8arr = new Uint8Array(bstr.length);
                                    for (let i = 0; i < bstr.length; i++) {
                                        u8arr[i] = bstr.charCodeAt(i);
                                    }
                                    fileToUpload = new Blob([u8arr], { type: mime });
                                } else {
                                    // Base64 string
                                    const bstr = atob(file);
                                    const u8arr = new Uint8Array(bstr.length);
                                    for (let i = 0; i < bstr.length; i++) {
                                        u8arr[i] = bstr.charCodeAt(i);
                                    }
                                    fileToUpload = new Blob([u8arr]);
                                }
                            } catch (e) {
                                return reject(new Error('Invalid file format'));
                            }
                        } else if (file?.data) {
                            fileToUpload = file.data;
                        } else if (file?.file) {
                            fileToUpload = file.file;
                        } else {
                            return reject(new Error('Unknown file format'));
                        }
                    }
                    
                    if (!(fileToUpload instanceof Blob) && !(fileToUpload instanceof File)) {
                        return reject(new Error('File must be a Blob or File object'));
                    }
                    
                    const formData = new FormData();
                    formData.append('file', fileToUpload, fileName);
                    formData.append('name', fileName);
                    formData.append('dir', '');
                    
                    fetch(url, {
                        method: 'POST',
                        headers: authHeaders,
                        body: formData
                    })
                    .then(response => {
                        if (!response.ok) {
                            return response.text().then(text => {
                                throw new Error(`Upload failed: ${response.status} - ${text}`);
                            });
                        }
                        return response.json();
                    })
                    .then(data => {
                        // Normalize response structure
                        const normalizedResponse = {
                            ...data,
                            // url is already correct from API: "https://api.techappforce.xyz/api/v1/File/GetFileFormIO/{mediaId}"
                            URL: data.mediaUrl || data.URL,  // FTP path for display/getFile
                            originalName: data.originalName || fileName,
                            name: data.name || fileName,
                            size: data.size || 0,
                            type: data.type || this.getFileType(data.originalName || fileName),
                            mediaUrl: data.mediaUrl,  // FTP path
                            mediaId: data.mediaId     // Keep mediaId
                        };
                        
                        resolve(normalizedResponse);
                    })
                    .catch(reject);
                });
            }
            /**
             * Override file download with authentication
             */
            private isOpeningFile: boolean = false;
            getFile(file: any): Promise<any> {
                const filePath = this.extractFilePath(file);
                
                if (!filePath || this.isOpeningFile) {
                    return Promise.resolve();
                }

                this.isOpeningFile = true;

                // Open file viewer popup using GetImage endpoint
                TabSdk.ui.showDialog({
                    screenId: TabCoreScreens.UploadedDocViewerScreen,
                    title: 'Open File Viewer',
                    width: '60%',
                    reqtokens: {
                        FileUrl: this.buildGetImageUrl(filePath),
                        FileName: file.name || file.originalName || file.DocumentName || file.MediaId?.Title || 'file'
                    }
                });

                setTimeout(() => this.isOpeningFile = false, 500);
                return Promise.resolve();
            }

            /**
             * Override file deletion with authentication
             */
            deleteFile(file: any): Promise<any> {
                if (this['component'].storage === 'url' && file.url) {
                    return fetch(file.url, {
                        method: 'DELETE',
                        headers: this.getAuthHeaders()
                    }).then(response => {
                        if (!response.ok) {
                            throw new Error(`Delete failed: ${response.statusText}`);
                        }
                        return response.json ? response.json() : {};
                    });
                }

                return super.deleteFile(file);
            }

            /**
             * Override file URL retrieval for authenticated image display
             */
            getFileUrl(file: any): Promise<string> {
                if (this['component'].image && this['component'].storage === 'url' && 
                    file && this.isImageFile(file)) {
                    return this.fetchFileWithCache(file);
                }

                return Promise.resolve(file.url || '');
            }

            /**
             * Override file value setting to handle authenticated images
             */
            setFileValue(file: any) {
                if (this['component'].image && this['component'].storage === 'url' && 
                    file && this.isImageFile(file)) {
                    this.fetchFileWithCache(file)
                        .then(blobUrl => {
                            super.setFileValue(blobUrl ? { ...file, url: blobUrl } : file);
                        })
                        .catch(error => {
                            console.error('Error loading file for preview:', error);
                            super.setFileValue(file);
                        });
                } else {
                    super.setFileValue(file);
                }
            }

            // ==================== XHR INTERCEPTION ====================

            /**
             * Setup XMLHttpRequest interception for file uploads with auth headers
             */
            private setupXHRInterception() {
                if ((window as any)._formioOriginalXHR) return; // Already setup
                
                const getAuthHeadersFunc = this.getAuthHeaders.bind(this);
                const OriginalXHR = window.XMLHttpRequest;
                (window as any)._formioOriginalXHR = OriginalXHR;
                
                (window as any).XMLHttpRequest = function() {
                    const xhr = new OriginalXHR();
                    const originalOpen = xhr.open;
                    const originalSend = xhr.send;
                    const originalSetRequestHeader = xhr.setRequestHeader;
                    
                    let requestUrl = '';
                    let requestMethod = '';
                    
                    xhr.open = function(method: string, url: string, ...args: any[]) {
                        requestUrl = url;
                        requestMethod = method.toUpperCase();
                        
                        // Block GET requests to GetFileFormIO
                        if (requestMethod === 'GET' && url.includes('GetFileFormIO')) {
                            xhr.abort = () => {};
                            xhr.send = () => {};
                            return;
                        }
                        
                        return (originalOpen as any).apply(xhr, arguments as any);
                    };
                    
                    xhr.setRequestHeader = function(name: string, value: string) {
                        return (originalSetRequestHeader as any).apply(xhr, [name, value]);
                    };
                    
                    xhr.send = function(data: any) {
                        // Inject auth headers for file uploads
                        if (data instanceof FormData && requestUrl.includes('UploadFormIO') && requestMethod === 'POST') {
                            const authHeaders = getAuthHeadersFunc();
                            Object.keys(authHeaders).forEach(key => {
                                try {
                                    originalSetRequestHeader.call(xhr, key, authHeaders[key]);
                                } catch (e) {
                                    console.error(`Failed to set header ${key}:`, e);
                                }
                            });
                        }
                        
                        return (originalSend as any).apply(xhr, [data]);
                    };
                    
                    return xhr;
                };
                
                // Copy static properties
                Object.keys(OriginalXHR).forEach(key => {
                    try {
                        (window.XMLHttpRequest as any)[key] = (OriginalXHR as any)[key];
                    } catch (e) {}
                });
            }

            /**
             * Override attach to setup XHR interception and image observers
             */
            attach(element: any) {
                if (this['component'].storage === 'url') {
                    this.setupXHRInterception();
                }
                
                const result = super.attach(element);
                
                this.setupImageObserver();
                
                if (this['component'].image && this['component'].storage === 'url' && this['dataValue']) {
                    setTimeout(() => this.loadAuthenticatedImages(), 100);
                } else if (!this['component'].image) {
                    setTimeout(() => this.convertImagesToLinksAndAttachHandlers(), 100);
                }
                
                return result;
            }

            /**
             * Setup mutation observer to intercept img elements
             */
            private setupImageObserver() {
                if (this.mutationObserver || !this['element']) return;

                this.mutationObserver = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'childList') {
                            mutation.addedNodes.forEach((node) => {
                                if (node.nodeName === 'IMG') {
                                    this.interceptImageSrc(node as HTMLImageElement);
                                } else if ((node as HTMLElement).querySelectorAll) {
                                    (node as HTMLElement).querySelectorAll('img').forEach(img => 
                                        this.interceptImageSrc(img)
                                    );
                                }
                            });
                        }
                        
                        if (mutation.type === 'attributes' && 
                            mutation.target.nodeName === 'IMG' && 
                            mutation.attributeName === 'src') {
                            this.interceptImageSrc(mutation.target as HTMLImageElement);
                        }
                    });
                });

                this.mutationObserver.observe(this['element'], {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['src']
                });
            }

            /**
             * Intercept image src to prevent unauthorized GET requests
             * Convert to link if image display is disabled, or load with auth if enabled
             */
            private interceptImageSrc(img: HTMLImageElement | any) {
                const imgSrc = img.src;
                
                // Skip blob URLs and empty/base URLs
                if (!imgSrc || imgSrc.startsWith('blob:') || 
                    imgSrc === window.location.href || 
                    imgSrc === `${window.location.origin}/`) {
                    
                    // Handle localhost base URL case
                    if ((imgSrc === `${window.location.origin}/` || imgSrc === window.location.href) && 
                        !img.dataset.processed) {
                        img.dataset.processed = 'true';
                        const files = this.getAllFiles();
                        if (files.length > 0 && this['component'].image && this.isImageFile(files[0])) {
                            this.fetchFileWithCache(files[0]).then(blobUrl => {
                                if (blobUrl) img.src = blobUrl;
                            }).catch(console.error);
                        }
                    }
                    return;
                }
                
                // Prevent reprocessing
                if (img.dataset.processed === 'true') return;
                img.dataset.processed = 'true';
                
                // Clear invalid src
                img.src = '';
                
                // Find matching file
                let matchingFile = this.findFileByUrl(imgSrc);
                if (!matchingFile) {
                    const files = this.getAllFiles();
                    matchingFile = !Array.isArray(this['dataValue']) ? this['dataValue'] : 
                                (files.length === 1 ? files[0] : null);
                }
                
                if (!matchingFile) return;
                
                // Convert to link if image display disabled
                if (!this['component'].image) {
                    const fileName = matchingFile.originalName || matchingFile.name || 'Download';
                    img.replaceWith(this.createFileLink(matchingFile, fileName));
                    return;
                }
                
                // Load authenticated image
                if (this.isImageFile(matchingFile)) {
                    this.fetchFileWithCache(matchingFile).then(blobUrl => {
                        if (blobUrl) img.src = blobUrl;
                    }).catch(console.error);
                }
            }

            /**
             * Create a downloadable file link with click handler
             */
            private createFileLink(file: any, fileName: string): HTMLAnchorElement {
                const link = document.createElement('a');
                link.href = file.url;
                link.setAttribute('ref', 'fileLink');
                link.textContent = fileName;
                link.onclick = (e) => {
                    e.preventDefault();
                    this.getFile(file);
                };
                return link;
            }

            /**
             * Convert existing img tags to download links and attach click handlers
             */
            private convertImagesToLinksAndAttachHandlers() {
                if (!this['element']) return;

                // Convert img tags to links
                this['element'].querySelectorAll('img[ref="fileImage"]').forEach((img: HTMLImageElement) => {
                    const imgSrc = img.getAttribute('src');
                    const fileName = img.getAttribute('alt') || 'Download';
                    
                    if (imgSrc) {
                        const matchingFile = this.findFileByUrl(imgSrc);
                        if (matchingFile) {
                            img.replaceWith(this.createFileLink(matchingFile, fileName));
                        }
                    }
                });

                // Attach click handlers to existing file links
                this['element'].querySelectorAll('a[ref="fileLink"]').forEach((link: HTMLAnchorElement) => {
                    if (link.onclick) return;
                    
                    const fileUrl = link.getAttribute('href');
                    if (fileUrl) {
                        const matchingFile = this.findFileByUrl(fileUrl);
                        if (matchingFile) {
                            link.onclick = (e) => {
                                e.preventDefault();
                                this.getFile(matchingFile);
                            };
                        }
                    }
                });
            }

            /**
             * Load authenticated images for existing file values
             */
            private loadAuthenticatedImages() {
                this.getAllFiles().forEach((file: any) => {
                    if (!file || !this.isImageFile(file)) return;

                    this.fetchFileWithCache(file)
                        .then(blobUrl => {
                            if (!blobUrl) return;
                            
                            this['element'].querySelectorAll('img').forEach((img: HTMLImageElement) => {
                                const fileUrl = file.url || file.URL || file.MediaId?.URL || '';
                                if (img.src.includes(fileUrl) || img.src.includes('GetFileFormIO')) {
                                    img.src = blobUrl;
                                }
                            });
                        })
                        .catch(console.error);
                });
            }

            // ==================== CLEANUP ====================

            /**
             * Cleanup mutation observer on detach
             */
            detach() {
                if (this.mutationObserver) {
                    this.mutationObserver.disconnect();
                    this.mutationObserver = null;
                }
                return super.detach();
            }
        }

        // Register the custom component
        (window as any).Formio.Components.components.file = TabCustomFile;
    }
}